import {
	Add,
	Edit,
	DeleteOutline,
	TooltipProvider,
	Tooltip,
	TooltipTrigger,
	TooltipContent,
	Email,
} from '@components/atoms';
import type { DataGridSelectedRowsMetadata } from '@components/organisms/data-grid/data-grid.type';
import { TableActionKey } from '@components/organisms/data-grid/data-grid.type';
import * as React from 'react';
import { withLineBreaks } from '../../../../../utils';

/**
 * Record mapping action keys to their corresponding icon components
 * Currently only supports 'create' action with Add icon
 */

const Icons: Record<TableActionKey, React.FC<React.SVGProps<SVGSVGElement>>> = {
	[TableActionKey.create]: Add,
	[TableActionKey.edit]: Edit,
	[TableActionKey.delete]: DeleteOutline,
	[TableActionKey.email]: Email,
} as const;

/**
 * Configuration for a single table action
 * @interface DataGridTableAction
 *
 * @property {string} key - Unique identifier for the action
 *   Must match a key in the Icons record to render the corresponding icon
 *
 * @property {() => void} onClick - Callback function executed when the action is clicked
 *   Handles the action's behavior
 *
 * @property {number} [size] - Optional size in pixels for the icon
 *   Defaults to DEFAULT_ICON_SIZE (20px) if not specified
 *
 * @property {React.FC<React.SVGProps<SVGSVGElement>>} [IconComponent] - Optional custom icon component
 *   If provided, this component will be used instead of the default icon from the Icons record
 *
 * @property {boolean} hidden - Whether the action should be hidden
 *  If true, the action will not be rendered in the action bar
 *
 * @property {string} [dataTestId] - Optional kebab-case test id rendered as `data-testid` on the action button
 */
export interface DataGridTableAction<T> {
	key: string;
	onClick: (selectedFields: DataGridSelectedRowsMetadata<T>) => void;
	size?: number;
	IconComponent?: React.FC<React.SVGProps<SVGSVGElement>>;
	hidden?: boolean;
	disabled?: boolean | string | ((metadata: DataGridSelectedRowsMetadata<T>) => boolean | string);
	ariaLabel?: string;
	tooltip?: string;
	disabledTooltip?: string;
	dataTestId?: string;
}

/**
 * Props for the TableActions component
 * @interface DataGridTableActions
 *
 * @property {DataGridTableAction[]} actions - Array of action configurations
 *   Each action will be rendered as a clickable icon in the action bar
 *
 * @property {DataGridSelectedRowsMetadata} selectionMetadata - Metadata for the selected rows
 *   Contains information about the selected rows and any active filters
 */
export interface DataGridTableActions<T> {
	actions: DataGridTableAction<T>[];
	selectionMetadata: DataGridSelectedRowsMetadata<T>;
}

/**
 * Default size in pixels for action icons when not specified in the action config
 */
const DEFAULT_ICON_SIZE = 20;

/**
 * A component that renders a horizontal bar of clickable action icons
 *
 * @component
 * @example
 * // Basic usage with a single create action
 * <TableActions
 *   actions={[
 *     {
 *       key: 'create',
 *       onClick: () => console.log('Create clicked')
 *     }
 *   ]}
 * />
 *
 * @example
 * // With custom icon size
 * <TableActions
 *   actions={[
 *     {
 *       key: 'create',
 *       onClick: () => console.log('Create clicked'),
 *       size: 24
 *     }
 *   ]}
 * />
 *
 * @example
 * // Multiple actions (note: currently only 'create' is supported)
 * <TableActions
 *   actions={[
 *     {
 *       key: 'create',
 *       onClick: handleCreate
 *     }
 *   ]}
 * />
 *
 * @example
 * // hidden action
 * <TableActions
 *   actions={[
 *    {
 *     key: 'create',
 *     onClick: handleCreate,
 *     hidden: true
 *   }]
 * />
 */
export function TableActions<T>({ actions, selectionMetadata }: DataGridTableActions<T>) {
	return (
		<div data-aid="data-grid-table-actions" className="flex flex-row-reverse gap-2">
			{actions.map(action => (
				<ActionButton key={action.key} action={action} selectionMetadata={selectionMetadata} />
			))}
		</div>
	);
}

interface ActionButtonProps<T> {
	action: DataGridTableAction<T>;
	selectionMetadata: DataGridSelectedRowsMetadata<T>;
}

function ActionButton<T>({ action, selectionMetadata }: ActionButtonProps<T>) {
	const IconComponent = action.IconComponent || Icons[action.key as keyof typeof Icons];

	if (!IconComponent || action.hidden) return null;

	const size = action.size ?? DEFAULT_ICON_SIZE;
	const dataId = `data-grid-table-action-${action.key}`;

	const disabled = typeof action.disabled === 'function' ? action.disabled(selectionMetadata) : action.disabled;

	const btn = (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
		<button
			data-aid={dataId}
			data-testid={action.dataTestId}
			aria-label={action.ariaLabel || action.key}
			onClick={() => action.onClick(selectionMetadata)}
			className="m-0 cursor-pointer border-none bg-transparent p-0 text-primary disabled:cursor-not-allowed disabled:opacity-30"
			disabled={!!disabled}
		>
			<IconComponent height={size} width={size} />
		</button>
	);

	return action.tooltip || typeof disabled === 'string' || action.disabledTooltip ? (
		<TooltipProvider delayDuration={0} skipDelayDuration={0}>
			<Tooltip>
				<TooltipTrigger asChild>{btn}</TooltipTrigger>
				<TooltipContent>
					{withLineBreaks(
						(disabled && typeof disabled === 'string' ? disabled : action.disabledTooltip) || action.tooltip
					)}
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	) : (
		btn
	);
}
